import { useCallback, useEffect, useRef } from 'react'
import type { NotificationEventEnvelope, UserNotification } from '../api/contracts'
import type { NotificationsDrawerProps } from '../components/notifications/NotificationsDrawer'
import type { NotificationToastStackProps } from '../components/notifications/NotificationToastStack'
import { resolveNotificationPath } from '../components/notifications/notificationUrl'
import { useNotificationToasts } from './useNotificationToasts'
import { useNotificationsFeed } from './useNotificationsFeed'
import { useUnreadNotificationsCount } from './useUnreadNotificationsCount'

interface UseNotificationsCenterOptions {
  apiBaseUrl: string
  /** False until the session allows the notifications API (for example, while a password change is required). */
  enabled: boolean
  isOpen: boolean
  onClose: () => void
  onNavigate: (path: string) => void
}

export interface NotificationsCenter {
  unreadCount: number
  drawerProps: NotificationsDrawerProps
  toastStackProps: NotificationToastStackProps
}

/**
 * Orchestration of the bell counter, the feed and the toasts: open a row, «Прочитать все», refresh and a toast on a
 * new notification. The counter owns the only `notification_created` subscription and passes each signal on.
 */
export function useNotificationsCenter({
  apiBaseUrl,
  enabled,
  isOpen,
  onClose,
  onNavigate
}: UseNotificationsCenterOptions): NotificationsCenter {
  const feed = useNotificationsFeed(apiBaseUrl, { active: enabled && isOpen })
  const refreshFirstPageRef = useRef(feed.refreshFirstPage)

  useEffect(() => {
    refreshFirstPageRef.current = feed.refreshFirstPage
  }, [feed.refreshFirstPage])

  const handleSignalCountChange = useCallback(() => {
    refreshFirstPageRef.current()
  }, [])

  const showToastRef = useRef<(event: NotificationEventEnvelope) => void>(() => {})
  const handleSignal = useCallback((event: NotificationEventEnvelope) => {
    showToastRef.current(event)
  }, [])

  const unread = useUnreadNotificationsCount(apiBaseUrl, {
    enabled,
    onSignalCountChange: handleSignalCountChange,
    onSignal: handleSignal
  })

  const { markRead, markAllRead } = feed
  const { decrement, refresh, reset } = unread

  const refreshCount = useCallback(() => {
    void refresh()
  }, [refresh])

  const toasts = useNotificationToasts({
    apiBaseUrl,
    enabled,
    onNavigate,
    onRead: decrement,
    onReadFailed: refreshCount
  })
  const { show: showToast, close: closeToast, closeAll: closeAllToasts } = toasts

  useEffect(() => {
    showToastRef.current = showToast
  }, [showToast])

  const handleOpenNotification = useCallback((notification: UserNotification) => {
    const path = resolveNotificationPath(notification)
    if (!path) {
      return
    }
    // A toast of the same notification would lower the count a second time.
    closeToast(notification.id)
    onClose()
    onNavigate(path)
    if (notification.readAtUtc !== null) {
      return
    }
    decrement()
    void markRead(notification.id).then((outcome) => {
      if (outcome !== 'read') {
        void refresh()
      }
    })
  }, [closeToast, decrement, markRead, onClose, onNavigate, refresh])

  const handleMarkAllRead = useCallback(() => {
    void markAllRead().then((succeeded) => {
      if (succeeded) {
        reset()
        closeAllToasts()
      }
    })
  }, [closeAllToasts, markAllRead, reset])

  return {
    unreadCount: unread.count,
    drawerProps: {
      open: enabled && isOpen,
      onClose,
      unreadCount: unread.count,
      feed,
      onOpenNotification: handleOpenNotification,
      onMarkAllRead: handleMarkAllRead
    },
    toastStackProps: {
      toasts: toasts.toasts,
      onOpen: toasts.open,
      onMarkRead: toasts.markRead,
      onClose: closeToast
    }
  }
}
